import { type FormEvent, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PERMISSIONS } from '@lms/shared-types';
import { ConfirmModal, Loader, useToast } from '@lms/ui';
import { HubBackLink } from '@/components/hub/HubBackLink';
import { usePermissions } from '@/hooks/usePermissions';
import { api } from '@/lib/api';
import { roleActionErrorToast } from '@/lib/user-feedback';
import type { FleetVehicle } from '@/features/practical-exams/PracticalExamVehiclesPage';

interface VehicleForm {
  plate: string;
  brand: string;
  model: string;
  year: string;
  color: string;
  transmission: 'mechanical' | 'automatic';
  notes: string;
  active: boolean;
}

const FIELD =
  'h-11 w-full rounded-lg border border-[#d1d5db] bg-white px-3.5 text-sm text-slate-900 outline-none transition-all placeholder:text-slate-400 focus:border-dash-primary focus:ring-1 focus:ring-dash-primary';
const LABEL = 'mb-1.5 block text-sm font-medium text-[#1e293b]';

function emptyForm(): VehicleForm {
  return {
    plate: '',
    brand: '',
    model: '',
    year: '',
    color: '',
    transmission: 'mechanical',
    notes: '',
    active: true,
  };
}

export function PracticalExamVehicleFormPage() {
  const { id } = useParams();
  const isNew = !id;
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { hasPermission } = usePermissions();
  const canSave = hasPermission(isNew ? PERMISSIONS.EVALUACIONES_CREAR : PERMISSIONS.EVALUACIONES_EDITAR);
  const canDelete = !isNew && hasPermission(PERMISSIONS.EVALUACIONES_ELIMINAR);
  const [form, setForm] = useState<VehicleForm>(emptyForm);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const query = useQuery({
    queryKey: ['practical-exam-vehicles', id],
    enabled: !isNew,
    queryFn: () => api<FleetVehicle>(`/practical-exams/vehicles/${id}`),
  });

  useEffect(() => {
    if (!query.data) return;
    setForm({
      plate: query.data.plate,
      brand: query.data.brand,
      model: query.data.model,
      year: query.data.year == null ? '' : String(query.data.year),
      color: query.data.color,
      transmission: query.data.transmission,
      notes: query.data.notes,
      active: query.data.active,
    });
  }, [query.data]);

  const save = useMutation({
    mutationFn: () =>
      api<FleetVehicle>(isNew ? '/practical-exams/vehicles' : `/practical-exams/vehicles/${id}`, {
        method: isNew ? 'POST' : 'PATCH',
        body: {
          ...form,
          plate: form.plate.toUpperCase(),
          year: form.year.trim() === '' ? null : Number(form.year),
        },
      }),
    onSuccess: async (saved) => {
      await queryClient.invalidateQueries({ queryKey: ['practical-exam-vehicles'] });
      toast({ title: 'Vehículo guardado', variant: 'success' });
      navigate(`/examen-practico/vehiculos/${saved.id}`, { replace: true });
    },
    onError: (error) => toast(roleActionErrorToast(error, 'No se pudo guardar el vehículo')),
  });

  const remove = useMutation({
    mutationFn: () => api(`/practical-exams/vehicles/${id}`, { method: 'DELETE' }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['practical-exam-vehicles'] });
      toast({ title: 'Vehículo eliminado', variant: 'success' });
      navigate('/examen-practico/vehiculos');
    },
    onError: (error) => toast(roleActionErrorToast(error, 'No se pudo eliminar el vehículo')),
  });

  function patch(partial: Partial<VehicleForm>) {
    setForm((current) => ({ ...current, ...partial }));
  }

  function submit(event: FormEvent) {
    event.preventDefault();
    if (!canSave) return;
    save.mutate();
  }

  if (!isNew && query.isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Loader />
      </div>
    );
  }

  return (
    <div className="lms-site-admin-hub-page font-sans">
      <div className="mb-6 flex items-start gap-4">
        <HubBackLink to="/examen-practico/vehiculos" label="Volver a vehículos" />
        <div>
          <h1 className="text-3xl font-semibold text-dash-primary md:text-4xl">
            {isNew ? 'Agregar vehículo' : form.plate || 'Vehículo'}
          </h1>
          <p className="mt-1 text-dash-on-surface-variant">
            La patente se guarda en mayúsculas, sin puntos ni guiones.
          </p>
        </div>
      </div>

      {!isNew && query.error ? (
        <p className="mb-4 text-sm text-error-600" role="alert">
          {(query.error as Error).message}
        </p>
      ) : null}

      <form onSubmit={submit} className="space-y-6 rounded-xl border border-dash-outline-variant bg-white p-6 shadow-sm sm:p-8">
        <div className="grid grid-cols-1 gap-x-6 gap-y-5 sm:grid-cols-2">
          <div>
            <label className={LABEL} htmlFor="vehicle-plate">
              Patente <span className="text-rose-500">*</span>
            </label>
            <div className="relative">
              <input
                id="vehicle-plate"
                required
                autoFocus
                maxLength={8}
                value={form.plate}
                disabled={!canSave}
                placeholder="Ej. ABCD12"
                onChange={(event) => patch({ plate: event.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '') })}
                className={`${FIELD} pr-10 font-mono uppercase tracking-wider`}
              />
              <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3.5 text-slate-400">
                <span className="material-symbols-outlined text-[19px]" aria-hidden="true">badge</span>
              </span>
            </div>
          </div>
          <div>
            <label className={LABEL} htmlFor="vehicle-year">
              Año <span className="text-rose-500">*</span>
            </label>
            <div className="relative">
              <input
                id="vehicle-year"
                required
                inputMode="numeric"
                value={form.year}
                disabled={!canSave}
                placeholder="Ej. 2024"
                onChange={(event) => patch({ year: event.target.value.replace(/\D/g, '').slice(0, 4) })}
                className={`${FIELD} pr-10`}
              />
              <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3.5 text-slate-400">
                <span className="material-symbols-outlined text-[19px]" aria-hidden="true">calendar_today</span>
              </span>
            </div>
          </div>
          <div>
            <label className={LABEL} htmlFor="vehicle-brand">
              Marca <span className="text-rose-500">*</span>
            </label>
            <input
              id="vehicle-brand"
              required
              value={form.brand}
              disabled={!canSave}
              placeholder="Ej. Chevrolet, Toyota, Hyundai"
              onChange={(event) => patch({ brand: event.target.value })}
              className={FIELD}
            />
          </div>
          <div>
            <label className={LABEL} htmlFor="vehicle-model">
              Modelo <span className="text-rose-500">*</span>
            </label>
            <input
              id="vehicle-model"
              required
              value={form.model}
              disabled={!canSave}
              placeholder="Ej. Sail, Yaris, Accent"
              onChange={(event) => patch({ model: event.target.value })}
              className={FIELD}
            />
          </div>
          <div>
            <label className={LABEL} htmlFor="vehicle-color">
              Color <span className="text-rose-500">*</span>
            </label>
            <div className="relative">
              <input
                id="vehicle-color"
                required
                value={form.color}
                disabled={!canSave}
                placeholder="Ej. Blanco, Gris Plata, Rojo"
                onChange={(event) => patch({ color: event.target.value })}
                className={`${FIELD} pr-10`}
              />
              <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3.5 text-slate-400">
                <span className="material-symbols-outlined text-[19px]" aria-hidden="true">palette</span>
              </span>
            </div>
          </div>
          <fieldset disabled={!canSave}>
            <legend className={LABEL}>
              Transmisión <span className="text-rose-500">*</span>
            </legend>
            <div className="grid h-11 grid-cols-2 gap-3">
              {(
                [
                  ['mechanical', 'Mecánico', 'tune'],
                  ['automatic', 'Automático', 'settings'],
                ] as const
              ).map(([value, label, icon]) => {
                const selected = form.transmission === value;
                return (
                  <label
                    key={value}
                    className={`flex cursor-pointer select-none items-center justify-center gap-2 rounded-lg border px-3 text-sm transition-all ${
                      selected
                        ? 'border-dash-primary bg-[#f0f3ff] font-semibold text-dash-primary'
                        : 'border-[#d1d5db] bg-white text-slate-700 hover:bg-slate-50'
                    } ${canSave ? '' : 'cursor-not-allowed opacity-60'}`}
                  >
                    <input
                      type="radio"
                      name="transmission"
                      value={value}
                      checked={selected}
                      onChange={() => patch({ transmission: value })}
                      className="sr-only"
                    />
                    <span className="material-symbols-outlined text-[18px]" aria-hidden="true">{icon}</span>
                    {label}
                  </label>
                );
              })}
            </div>
          </fieldset>
        </div>

        <div className="border-t border-slate-100 pt-3">
          <div className="flex items-center justify-between rounded-xl border border-slate-200 bg-slate-50/80 p-4">
            <div className="flex items-center gap-3">
              <div className="flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 bg-white text-dash-primary shadow-2xs">
                <span className="material-symbols-outlined text-[20px]" aria-hidden="true">directions_car</span>
              </div>
              <div>
                <label className="block cursor-pointer text-sm font-semibold text-slate-900" htmlFor="vehicle-status-toggle">
                  Disponible para examen práctico
                </label>
                <p className="mt-0.5 text-xs text-slate-500">
                  El vehículo podrá ser seleccionado al programar exámenes prácticos.
                </p>
              </div>
            </div>
            <label className={`relative ml-4 inline-flex shrink-0 items-center ${canSave ? 'cursor-pointer' : 'cursor-not-allowed'}`}>
              <input
                id="vehicle-status-toggle"
                type="checkbox"
                checked={form.active}
                disabled={!canSave}
                onChange={(event) => patch({ active: event.target.checked })}
                className="peer sr-only"
              />
              <span className="relative h-6 w-11 rounded-full bg-slate-300 after:absolute after:left-[2px] after:top-[2px] after:h-5 after:w-5 after:rounded-full after:border after:border-slate-300 after:bg-white after:transition-all after:content-[''] peer-checked:bg-dash-primary peer-checked:after:translate-x-full peer-checked:after:border-white" />
            </label>
          </div>
        </div>

        <div>
          <label className={LABEL} htmlFor="vehicle-observations">
            Observaciones
          </label>
          <textarea
            id="vehicle-observations"
            value={form.notes}
            disabled={!canSave}
            rows={3}
            placeholder="Detalles sobre doble comando pedagógico, póliza de seguro, mantención o condiciones particulares..."
            onChange={(event) => patch({ notes: event.target.value })}
            className="w-full resize-y rounded-lg border border-[#d1d5db] bg-white px-3.5 py-2.5 text-sm text-slate-900 outline-none transition-all placeholder:text-slate-400 focus:border-dash-primary focus:ring-1 focus:ring-dash-primary"
          />
          <p className="mt-1.5 text-xs text-slate-500">
            Información adicional visible para inspectores durante la evaluación.
          </p>
        </div>

        <div className="flex flex-col-reverse items-center justify-end gap-3 border-t border-slate-100 pt-5 sm:flex-row">
          {canDelete ? (
            <button
              type="button"
              onClick={() => setConfirmDelete(true)}
              className="w-full rounded-lg border border-error-600 px-5 py-2.5 text-sm font-medium text-error-600 sm:mr-auto sm:w-auto"
            >
              Eliminar
            </button>
          ) : null}
          <Link
            to="/examen-practico/vehiculos"
            className="w-full rounded-lg border border-[#d1d5db] bg-white px-5 py-2.5 text-center text-sm font-medium text-slate-700 transition-all hover:bg-slate-50 sm:w-auto"
          >
            Cancelar
          </Link>
          {canSave ? (
            <button
              type="submit"
              disabled={save.isPending}
              className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-dash-primary px-6 py-2.5 text-sm font-medium text-white shadow-sm transition-all hover:bg-dash-primary/90 disabled:opacity-60 sm:w-auto"
            >
              <span className="material-symbols-outlined text-[19px]" aria-hidden="true">save</span>
              {save.isPending ? 'Guardando…' : 'Guardar vehículo'}
            </button>
          ) : null}
        </div>
      </form>

      <ConfirmModal
        open={confirmDelete}
        title="Eliminar vehículo"
        description="Se borra este vehículo. Esta acción no se puede deshacer."
        confirmLabel="Eliminar"
        loading={remove.isPending}
        onClose={() => setConfirmDelete(false)}
        onConfirm={() => remove.mutate()}
      />
    </div>
  );
}
